import { AxiosResponse } from 'axios'
import EventConstants from '../../constants/EventConstants'
import { AxiosUtil } from '../../utils/axios-util'
import DataValidationUtil from '../../utils/DataValidationUtil'
import {
  errorNotification,
  errorResponseNotification,
  successNotification,
} from '../notification-service'
import { cloneImpressions } from '../products-service'
import { saveCfImpressionMappings } from './user-settings-service'

interface AddProductsParams {
  request: any
  isCFProduct?: boolean
  showNotification?: boolean
  shouldUpdateDescriptionForBps?: boolean
  autoDrill?: boolean
  autoPick?: boolean
  bidId?: string | null
}

interface AddNewApparelProductsParams {
  programs: any[]
  eventId: string
  planId: number | string | null
  dept: string
  clazz: string
  bpIds: number[] | null
  bidId?: null | string
  autoPick?: boolean
}

export const addProducts = ({
  request,
  isCFProduct = false,
  showNotification = true,
  shouldUpdateDescriptionForBps = true,
  autoDrill = true,
  autoPick = false,
  bidId = null,
}: AddProductsParams) => {
  if (isCFProduct) {
    return AxiosUtil.post(
      `${EventConstants.COMMON}/createCarryforwardItems`,
      request,
    )
      .then(res => {
        if (showNotification) {
          successNotification(res.data.message)
        }
        return res
      })
      .catch(err => {
        if (showNotification) {
          errorResponseNotification(err)
        }
        throw err
      })
  }
  return AxiosUtil.post(
    `${EventConstants.COMMON}/createNewItems?should_update_description_for_bps=${shouldUpdateDescriptionForBps}&auto_drill=${autoDrill}&auto_pick=${autoPick}&bid_id=${bidId}`,
    request,
  )
    .then(res => {
      if (showNotification) {
        successNotification('Successfully added products')
      }
      return res
    })
    .catch(err => {
      if (showNotification) {
        errorResponseNotification(err)
      }
      throw err
    })
}

export const buildRequest = ({ addByOption, eventId, planId, bps }) => {
  switch (addByOption.option) {
    case 'new':
      return {
        item: {
          event_id: eventId,
          plan_id: planId,
          section_data: {
            item: {
              description: 'Placeholder',
              dept: addByOption.dept,
              clazz: addByOption.class,
              no_impression: addByOption.noOfImpressions,
            },
          },
        },
        num_items_to_create: addByOption.noOfItems,
        bp_ids: bps.map(bp => bp.data.bp_id),
      }
    case 'dpci':
      return {
        items: stringToArray(addByOption.dpci).map(itemId => {
          return {
            [addByOption.option]: itemId,
          }
        }),
        event_id: eventId,
        plan_id: planId,
      }
    case 'tcin':
      return {
        items: stringToArray(addByOption.tcin).map(itemId => {
          return {
            [addByOption.option]: itemId,
          }
        }),
        event_id: eventId,
        plan_id: planId,
      }
    default:
      return {}
  }
}

export const addNewApparelProducts = ({
  programs,
  eventId,
  planId,
  dept,
  clazz,
  bpIds,
  bidId = null,
  autoPick = false,
}: AddNewApparelProductsParams) => {
  const requests = programs.map(data => {
    return {
      item: {
        id: data.item_bp_id,
        event_id: eventId,
        plan_id: planId,
        section_data: {
          ...data.section_data,
          item: {
            ...data.section_data.item,
            dept,
            clazz,
            num_carry_forward_impression:
              (data.section_data.item.no_impression ?? 0) -
              (data.section_data.item.num_new_impression ?? 0),
          },
        },
      },
      num_items_to_create: 1,
      bp_ids: bpIds ?? data.bp_ids,
    }
  })
  const promises: Promise<AxiosResponse<any>>[] = requests.map(request =>
    addProducts({
      request,
      isCFProduct: false,
      showNotification: false,
      shouldUpdateDescriptionForBps: false,
      bidId,
      autoDrill: true,
      autoPick,
    }),
  )
  return Promise.allSettled(promises).then(responses => {
    const rowData = responses.reduce((acc: any, response) => {
      if (response.status === 'rejected') {
        acc.push(JSON.parse(response.reason.config.data).item)
      }
      return acc
    }, [])
    if (rowData.length > 0) {
      errorNotification(
        [
          'One or more programs could not be added. Please try again! If Problem persists, please contact support',
        ],
        5000,
      )
    } else {
      successNotification('Successfully added the programs')
    }
    return rowData
  })
}

export const groupDuplicateAndValidPrograms = ({
  existingProgramNames,
  newProgramItemBps,
}) => {
  return newProgramItemBps.reduce(
    (acc, newProgramItemBp) => {
      if (
        existingProgramNames.some(
          existingProgram =>
            existingProgram.toLowerCase().trim() ===
            newProgramItemBp.section_data.item.description.toLowerCase().trim(),
        )
      ) {
        acc.duplicateProgramItemBps.push(newProgramItemBp)
      } else {
        acc.validProgramsItemBps.push(newProgramItemBp)
      }
      return acc
    },
    { duplicateProgramItemBps: [], validProgramsItemBps: [] },
  )
}

export const groupDuplicateAndValidProgramsWithBps = ({
  existingProgramsWithBps,
  newProgramItemBps,
  selectedBps,
}) => {
  return newProgramItemBps.reduce(
    (acc, newProgramItemBp) => {
      const existingProgram = existingProgramsWithBps.find(
        existingProgram =>
          existingProgram.programName.toLowerCase().trim() ===
          newProgramItemBp.section_data.item.description.toLowerCase().trim(),
      )
      if (existingProgram) {
        const validBps = selectedBps.filter(
          selectedBp => !existingProgram.bpIds.includes(selectedBp.bpId),
        )
        const duplicateBps = selectedBps.filter(selectedBp =>
          existingProgram.bpIds.includes(selectedBp.bpId),
        )
        if (validBps.length > 0) {
          acc.validProgramsItemBps.push({
            ...newProgramItemBp,
            bp_ids: validBps.map(bp => bp.bpId),
          })
        }
        if (duplicateBps.length > 0) {
          acc.duplicateProgramItemBps.push({
            ...newProgramItemBp,
            bp_ids: duplicateBps.map(bp => bp.bpId),
            bp_names: duplicateBps.map(bp => bp.bpName),
          })
        }
      } else {
        acc.validProgramsItemBps.push({
          ...newProgramItemBp,
          bp_ids: selectedBps.map(bp => bp.bpId),
        })
      }
      return acc
    },
    { duplicateProgramItemBps: [], validProgramsItemBps: [] },
  )
}

export const addCarryForwardImpressions = (
  selectedNodes,
  newEventId,
  cfImpressionProgramListRef,
  gridApi,
) => {
  const requests = selectedNodes.reduce((acc, node) => {
    if (!DataValidationUtil.isUndefinedOrNullOrBlank(node.data.newProgram)) {
      acc.push({
        item_bp_id: node.data.id,
        number_of_copies: 1,
        new_program_id: node.data.newProgram,
        new_event_id: newEventId,
      })
    }
    return acc
  }, [])
  let atLeastOneFailed = false
  cloneImpressions(requests).then(res => {
    requests.forEach(request => {
      if (res?.[request.item_bp_id] === 'SUCCESS') {
        cfImpressionProgramListRef.current.push({
          programItemBpId: request.new_program_id,
          itemBpId: request.item_bp_id,
        })
        gridApi.getRowNode(request.item_bp_id).setSelected(false)
      } else {
        atLeastOneFailed = true
      }
    })
    if (atLeastOneFailed) {
      errorNotification([
        'Some of the impressions could not be added. Failed ones are still selected. Please try again. If the problem persists, please contact support',
      ])
    }
    saveCfImpressionMappings({
      eventId: newEventId,
      programImpressions: cfImpressionProgramListRef.current,
    })
    gridApi.redrawRows({ rowNodes: selectedNodes })
  })
}

const stringToArray = (stringToSplit: string) => {
  return stringToSplit.trim().split(/[\s,]+/) // split on commas or spaces
}
